import { useMutation } from "@apollo/client";
import { Camera, Upload, X, Loader2 } from "lucide-react";
import { useState } from "react";
import { ErrorCode } from "react-dropzone";
import { z } from "zod";
import { type userProfileOverviewQuery } from "./UserProfile";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Dropzone, DropzoneDescription, DropzoneInput, DropzoneTitle, DropzoneZone } from "@/components/ui/dropzone";
import { graphql, type ResultOf } from "@/graphql";
import { useToast } from "@/hooks/use-toast";
import { extractErrorMessages, getErrorMessage } from "@/lib/utils/errors";
import { getUserInitials } from "@/settings/utils";

const MAX_FILE_SIZE = 5000000; // 5MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const avatarUploadSchema = z.object({
  image: z
    .instanceof(File)
    .refine((file) => file.size <= MAX_FILE_SIZE, "File must be less than 5MB")
    .refine((file) => ACCEPTED_IMAGE_TYPES.includes(file.type), "Only .jpg, .jpeg, .png, and .webp files are accepted"),
});

interface AvatarUploadProps {
  user: NonNullable<ResultOf<typeof userProfileOverviewQuery>["currentUser"]>;
  onSuccess?: () => void;
}

export function AvatarUpload({ user, onSuccess }: AvatarUploadProps) {
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { toast } = useToast();

  const [updateUserAvatar, { loading }] = useMutation(updateUserAvatarMutation);

  const handleFileSelect = (file: File) => {
    const result = avatarUploadSchema.safeParse({ image: file });

    if (!result.success) {
      toast({
        title: "Invalid file",
        description: extractErrorMessages(result.error) || "Please select a valid image file.",
        variant: "destructive",
      });
      return;
    }

    setSelectedFile(file);

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Please select a file to upload.",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await updateUserAvatar({
        variables: {
          input: {
            imageFile: selectedFile,
            // Backend treats missing fields as "set to null" rather than "leave unchanged"
            // We must pass all user fields to prevent data loss when updating the avatar
            firstName: user.firstName,
            lastName: user.lastName,
            jobTitle: user.jobTitle,
            about: user.about,
            phone: user.phone,
          },
        },
      });

      const errors = result.data?.updateUserInfo?.errors || [];

      if (errors.length > 0) {
        toast({
          title: "Failed to update avatar",
          description: extractErrorMessages(errors),
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Avatar updated",
        description: "Your profile picture has been successfully updated.",
      });
      handleClose();
      onSuccess?.();
    } catch (error) {
      toast({
        title: "Failed to update avatar",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    setIsUploadOpen(false);
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  return (
    <>
      <div className="relative group">
        <Avatar className="h-24 w-24">
          <AvatarImage src={user.image || undefined} alt={`${user.firstName} ${user.lastName}`} />
          <AvatarFallback className="text-lg">{getUserInitials(user.firstName, user.lastName)}</AvatarFallback>
        </Avatar>

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-full flex items-center justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsUploadOpen(true)}
            className="text-white hover:text-white hover:bg-white/20"
          >
            <Camera className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Dialog open={isUploadOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Update Profile Picture</DialogTitle>
            <DialogDescription>Upload a new profile picture.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Current/Preview Avatar */}
            <div className="flex justify-center">
              <Avatar className="h-24 w-24">
                <AvatarImage src={previewUrl || user.image || undefined} alt={`${user.firstName} ${user.lastName}`} />
                <AvatarFallback className="text-lg">{getUserInitials(user.firstName, user.lastName)}</AvatarFallback>
              </Avatar>
            </div>

            {/* File Upload Zone */}
            {selectedFile ? null : (
              <Dropzone
                maxSize={MAX_FILE_SIZE}
                accept={{
                  "image/*": ACCEPTED_IMAGE_TYPES.map((type) => type.replace("image/", ".")),
                }}
                onDropAccepted={(acceptedFiles) => {
                  const file = acceptedFiles[0];
                  if (file) {
                    handleFileSelect(file);
                  }
                }}
                onDropRejected={(fileRejections) => {
                  fileRejections.forEach((fileRejection) => {
                    if (fileRejection.errors.some((err) => err.code === ErrorCode.FileTooLarge)) {
                      toast({
                        title: "File too large",
                        description: "Please select an image smaller than 5MB.",
                        variant: "destructive",
                      });
                    } else {
                      toast({
                        title: "Invalid file",
                        description: "Please select a valid image file (JPG, PNG, WebP).",
                        variant: "destructive",
                      });
                    }
                  });
                }}
              >
                <DropzoneZone className="flex justify-center bg-muted/50 hover:bg-muted hover:border-accent-foreground/50 transition-colors">
                  <DropzoneInput />
                  <div className="flex flex-col items-center gap-2 py-8">
                    <Upload className="h-8 w-8 text-muted-foreground" />
                    <DropzoneTitle className="text-sm font-medium">Click to upload or drag and drop</DropzoneTitle>
                    <DropzoneDescription className="text-xs">PNG, JPG, WebP up to 5MB</DropzoneDescription>
                  </div>
                </DropzoneZone>
              </Dropzone>
            )}

            {/* Selected File Info */}
            {selectedFile ? (
              <div className="bg-muted p-3 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{selectedFile.name}</p>
                    <p className="text-xs text-muted-foreground">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedFile(null);
                      if (previewUrl) {
                        URL.revokeObjectURL(previewUrl);
                        setPreviewUrl(null);
                      }
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={!selectedFile || loading} className="gap-2">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export const updateUserAvatarMutation = graphql(`
  mutation UpdateUserAvatar($input: UpdateUserInput!) {
    updateUserInfo(input: $input) {
      currentUser {
        id
        image
      }
      errors {
        message
      }
    }
  }
`);
